import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook that syncs MIDI root note events to an <audio> element's playback time.
 * Uses requestAnimationFrame for tight sync (~16ms resolution).
 * The rAF loop only runs while audio is playing to avoid CPU drain.
 * @param {HTMLAudioElement|null} audioElement
 * @param {Array<{time: number, note: string}>|null} events - sorted by time
 * @param {Function} onChordChange - called with (noteName: string)
 */
export function useMidiSync(audioElement, events, onChordChange) {
  const lastIndexRef = useRef(-1);
  const rafRef = useRef(null);
  const onChordChangeRef = useRef(onChordChange);
  const eventsRef = useRef(events);

  // Keep refs current to avoid stale closures in the rAF loop
  useEffect(() => { onChordChangeRef.current = onChordChange; }, [onChordChange]);
  useEffect(() => { eventsRef.current = events; }, [events]);

  const startLoop = useCallback((audio) => {
    const tick = () => {
      const currentEvents = eventsRef.current;
      if (!audio.paused && currentEvents && currentEvents.length > 0) {
        const t = audio.currentTime;

        // Binary search: find last event where time <= t
        let idx = -1;
        let lo = 0, hi = currentEvents.length - 1;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (currentEvents[mid].time <= t) {
            idx = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }

        if (idx !== lastIndexRef.current && idx >= 0) {
          lastIndexRef.current = idx;
          onChordChangeRef.current(currentEvents[idx].note);
        }

        rafRef.current = requestAnimationFrame(tick);
      }
      // When paused: don't schedule next frame — loop stops naturally
    };

    // Cancel any existing loop before starting a new one
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!audioElement || !events || events.length === 0) return;

    const handlePlay = () => startLoop(audioElement);
    const handlePause = () => stopLoop();
    const handleEnded = () => stopLoop();
    const handleSeeked = () => { lastIndexRef.current = -1; };

    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('seeked', handleSeeked);

    // If audio is already playing when the hook mounts, start immediately
    if (!audioElement.paused) {
      startLoop(audioElement);
    }

    return () => {
      stopLoop();
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('seeked', handleSeeked);
      lastIndexRef.current = -1;
    };
  }, [audioElement, events, startLoop, stopLoop]);
}
